import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';

import {
  TICKET_CORPORATE_CREDIT_ENDPOINT,
  generateEndpointTicketCorporateCreditToAddCreditLine,
  generateEndpointTicketCorporateCreditToUnifyInvoices,
} from '../../config/endpoints';

import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';

import { isErrorResponse } from '../../utils/error-handlers';

import {
  TICKET_CORPORATE_CREDIT_PATH,
  MANUAL_VOUCHER_PATH,
} from '../../config/paths';
import {
  CLEAR_TICKET_CORPORATE_CREDIT,
  CLEAR_TICKET_CORPORATE_CREDIT_MANUAL_VOUCHER_VALUES,
  CLEAR_TICKET_CORPORATE_CREDITS,
  CLEAR_TICKET_CORPORATE_INVOICES,
  FLAG_GETTING_TICKET_CORPORATE_CREDITS,
  FLAG_GETTING_TICKET_CORPORATE_INVOICES,
  FLAG_TICKET_CORPORATE_CREDIT_ACTIVITY,
  GET_TICKET_CORPORATE_CREDIT,
  GET_TICKET_CORPORATE_CREDIT_MANUAL_VOUCHER_VALUES,
  GET_TICKET_CORPORATE_CREDITS,
  GET_TICKET_CORPORATE_INVOICES,
} from '../types/corporate/TicketCorporateCredit';

const flagGettingTicketCorporateCredits = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TICKET_CORPORATE_CREDITS,
    payload: flag,
  });

const getTicketCorporateCredits = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingTicketCorporateCredits(true));

    const url = `${TICKET_CORPORATE_CREDIT_ENDPOINT}?${QueryString.stringify(
      tableFilters,
    )}`;

    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const ticketCorporateCredits = await response.json();

    dispatch({
      type: GET_TICKET_CORPORATE_CREDITS,
      payload: ticketCorporateCredits,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingTicketCorporateCredits(false));
  }
};

const clearTicketCorporateCredits = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET_CORPORATE_CREDITS,
  });

const flagTicketCorporateCreditActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_TICKET_CORPORATE_CREDIT_ACTIVITY,
    payload: flag,
  });

const getTicketCorporateCredit =
  async ({ ticketCorporateCreditId }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const url = `${TICKET_CORPORATE_CREDIT_ENDPOINT}/${ticketCorporateCreditId}`;

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const ticketCorporateCredit = await response.json();

      dispatch({
        type: GET_TICKET_CORPORATE_CREDIT,
        payload: ticketCorporateCredit,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

const clearTicketCorporateCredit = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET_CORPORATE_CREDIT,
  });

const postTicketCorporateCredit =
  async ({
    name,
    description,
    active,
    companyId,
    initialBalance,
    actualBalance,
    creditDays,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const payload = {
        name,
        description,
        active,
        companyId,
        initialBalance,
        actualBalance,
        creditDays,
      };

      const response = await fetch(TICKET_CORPORATE_CREDIT_ENDPOINT, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const ticketCorporateCredit = await response.json();

      dispatch(
        push(`${TICKET_CORPORATE_CREDIT_PATH}/${ticketCorporateCredit.id}`),
      );
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

const putTicketCorporateCredit =
  async (
    ticketCorporateCreditId,
    {
      name,
      description,
      active,
      companyId,
      initialBalance,
      actualBalance,
      creditDays,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const payload = {
        id: ticketCorporateCreditId,
        name,
        description,
        active,
        companyId,
        initialBalance,
        actualBalance,
        creditDays,
      };

      const url = `${TICKET_CORPORATE_CREDIT_ENDPOINT}/${ticketCorporateCreditId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const ticketCorporateCredit = await response.json();

      dispatch(
        push(`${TICKET_CORPORATE_CREDIT_PATH}/${ticketCorporateCredit.id}`),
      );
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

const deleteTicketCorporateCredit =
  async ({ ticketCorporateCreditId }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const url = `${TICKET_CORPORATE_CREDIT_ENDPOINT}/${ticketCorporateCreditId}`;

      const response = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      dispatch(push(TICKET_CORPORATE_CREDIT_PATH));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

const flagGettingTicketCorporateInvoices = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TICKET_CORPORATE_INVOICES,
    payload: flag,
  });

const getTicketCorporateInvoicesByCompanyId =
  async ({ companyId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTicketCorporateInvoices(true));

      const url = `${TICKET_CORPORATE_CREDIT_ENDPOINT}/company/${companyId}/tickets`;

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const tickets = await response.json();

      dispatch({
        type: GET_TICKET_CORPORATE_INVOICES,
        payload: tickets,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingTicketCorporateInvoices(false));
    }
  };

const clearTicketCorporateInvoices = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET_CORPORATE_INVOICES,
  });

const putTicketCorporateInvoices =
  async ({ ticketIds, ticketCorporateCreditId }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const url = generateEndpointTicketCorporateCreditToUnifyInvoices(
        ticketCorporateCreditId,
      );

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(ticketIds),
      });

      await isErrorResponse(response, null, dispatch);

      const payload = await response.json();

      dispatch({
        type: GET_TICKET_CORPORATE_CREDIT_MANUAL_VOUCHER_VALUES,
        payload,
      });

      dispatch(push(`${MANUAL_VOUCHER_PATH}/new`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

const clearTicketCorporateCreditManualVoucherValues = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET_CORPORATE_CREDIT_MANUAL_VOUCHER_VALUES,
  });

const patchTicketCorporateCreditCreditLine =
  async ({ ticketCorporateCreditId, amount }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketCorporateCreditActivity(true));

      const url = generateEndpointTicketCorporateCreditToAddCreditLine(
        ticketCorporateCreditId,
        amount,
      );

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        method: 'PATCH',
      });

      await isErrorResponse(response, null, dispatch);

      const ticketCorporateCredit = await response.json();

      dispatch({
        type: GET_TICKET_CORPORATE_CREDIT,
        payload: ticketCorporateCredit,
      });

      toastr.success('Éxito', 'Línea de crédito aumentada correctamente');

      return ticketCorporateCredit;
    } catch (error) {
      toastr.error('Error', error.message);

      return null;
    } finally {
      dispatch(flagTicketCorporateCreditActivity(false));
    }
  };

export {
  getTicketCorporateCredits,
  clearTicketCorporateCredits,
  getTicketCorporateCredit,
  clearTicketCorporateCredit,
  postTicketCorporateCredit,
  putTicketCorporateCredit,
  deleteTicketCorporateCredit,
  flagGettingTicketCorporateInvoices,
  getTicketCorporateInvoicesByCompanyId,
  clearTicketCorporateInvoices,
  putTicketCorporateInvoices,
  clearTicketCorporateCreditManualVoucherValues,
  patchTicketCorporateCreditCreditLine,
};
